/** What the lesson showed about this student, once their teacher has released it.
 *
 * THE RELEASE REACHES THE STUDENT THROUGH THE SERVER, never through the device, and there are
 * two ways it can arrive; either one opens this panel and neither is a guess:
 *
 * 1. `released` on the student's own state frame. The teacher's `release_summary` appends
 *    `summary_released` and the hub broadcasts a fresh frame to everyone in the room, so this
 *    is the instant, session-scoped signal. TODAY THE HUB ONLY PUTS THAT FIELD ON STAFF
 *    FRAMES (services/session hub.py, inside `if staff:`), so it never arrives on a phone;
 *    moving that one line out of the staff block is the server side of this fix.
 * 2. The student's own released record, `/analytics/v1/me/mastery`. That endpoint IS the
 *    `report_release` policy: a course that releases per session is absent until its teacher
 *    releases one, and a `guest_only` course has no subject and can never appear. So its
 *    answer arriving with a course in it is the service saying the student may look.
 *
 * The second route is course-scoped, not session-scoped, and that is its one imprecision: in
 * the second lesson of a course whose first lesson was released, the panel opens as soon as
 * this lesson ends. What it then shows is still only the server's: released evidence only in
 * the objectives, and a recap of verdicts this student was already given live, in front of the
 * whole class. The imprecision disappears the moment `released` ships on the student frame.
 *
 * Everything shown is the student's own and nothing else exists to show: the objectives come
 * back keyed to the caller's subject, and the lesson recap repeats what the server already
 * told this device about its own answers (src/student/lessonRecord.ts). No peer, no name, no
 * class average — the aggregate a student may see is the one the teacher reveals live.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { BAND_GLYPH, BAND_TONE, useMine } from "./mine";
import { lessonRounds, tally, type Round } from "./lessonRecord";

/** The verdict as the server stated it, with a glyph AND a word: never colour alone. */
function verdictOf(r: Round): { key: string; glyph: string; tone: string } {
  if (r.correct === true) return { key: "right", glyph: "✓", tone: "var(--secure)" };
  if (r.correct === false) return { key: "wrong", glyph: "✗", tone: "var(--not-yet)" };
  return r.answered
    ? { key: "answered", glyph: "●", tone: "var(--ink-2)" }
    : { key: "missed", glyph: "○", tone: "var(--ink-2)" };
}

export function LessonResult({
  sessionId,
  guestToken,
  released,
}: {
  sessionId: string;
  guestToken: string | null;
  /** The session's own release signal, when the server sends it (see the note above). */
  released: boolean;
}) {
  const { t } = useTranslation();
  const mine = useMine(guestToken);
  const courses = mine.data?.courses ?? [];
  const open = released || courses.length > 0;
  // Two reasons to keep asking, both of them the server's timing and not the student's:
  // the teacher may release minutes after the bell, and the mastery projection is fed by the
  // event relay, so the objectives of the lesson that just ended land a moment after the
  // release does. So: ask every fifteen seconds while the answer is still "not yet", and go
  // on asking for two minutes after it is not, then stop.
  const refetch = mine.refetch;
  const settleUntil = useRef<number | null>(null);
  if (open && settleUntil.current === null) settleUntil.current = Date.now() + 120_000;
  useEffect(() => {
    const id = window.setInterval(() => {
      if (settleUntil.current !== null && Date.now() > settleUntil.current) return;
      void refetch();
    }, 15_000);
    return () => window.clearInterval(id);
  }, [refetch]);
  // The frame that says "released" is also the frame to re-read the record on.
  useEffect(() => {
    if (released) void refetch();
  }, [released, refetch]);

  const rounds = lessonRounds(sessionId);
  const sums = tally(rounds);
  const assessed = courses
    .map((c) => ({ ...c, seen: c.los.filter((lo) => lo.n > 0) }))
    .filter((c) => c.seen.length > 0);

  // Both states are a <section aria-live> at the same position, so React updates one element
  // rather than swapping two: a live region that is unmounted and replaced announces nothing,
  // and the arrival of the result is exactly what a student on a screen reader is waiting for.
  if (!open) {
    // Three different silences, and none of them may be told as another. `no_principal` is
    // this device holding no identity for the class (a `guest_only` course, or a lesson run
    // from the teacher's own computer, which mints none); any other error is the record being
    // out of reach, which is not the teacher withholding it; and no error at all is the
    // teacher simply not having released yet.
    const noPrincipal = mine.error instanceof ApiError && mine.error.code === "no_principal";
    const unreachable = !!mine.error && !noPrincipal;
    return (
      <section aria-live="polite" className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm">
        <h2 className="font-semibold">{t("student.result.title")}</h2>
        <p className="mt-1 text-[color:var(--ink-2)]">
          {mine.isPending
            ? t("app.loading")
            : noPrincipal
              ? t("student.result.noRecord")
              : unreachable
                ? t("student.result.unreachable")
                : t("student.result.waiting")}
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4" aria-live="polite">
      <div className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
        <h2 className="font-semibold">{t("student.result.title")}</h2>
        {rounds.length === 0 ? (
          <p className="mt-1 text-sm text-[color:var(--ink-2)]">{t("student.result.noQuestions")}</p>
        ) : (
          <>
            <p className="mt-1 text-sm text-[color:var(--ink-2)]">
              {t("student.result.tally", { answered: sums.answered, asked: sums.asked })}
              {sums.judged > 0 && ` · ${t("student.result.judged", { right: sums.right, judged: sums.judged })}`}
            </p>
            <ol className="mt-3 flex flex-col gap-2">
              {rounds.map((r, i) => {
                const v = verdictOf(r);
                return (
                  <li key={`${r.item_id}:${r.attempt}`} className="flex items-start gap-3 text-sm">
                    <span aria-hidden className="w-4 shrink-0 text-base leading-6" style={{ color: v.tone }}>
                      {v.glyph}
                    </span>
                    <span className="min-w-0 flex-1 break-words">
                      {r.stem || t("student.result.spoken", { n: i + 1 })}
                    </span>
                    <span className="shrink-0 text-xs text-[color:var(--ink-2)]">{t(`student.result.${v.key}`)}</span>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>

      {/* A course the student has no assessed objective in is not a card with nothing in it:
          the record only speaks where there is evidence. */}
      {assessed.length === 0 && <p className="text-sm text-[color:var(--ink-2)]">{t("student.result.noEvidence")}</p>}
      {assessed.map((c) => (
        <div key={c.course_id} className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
          <h2 className="font-semibold break-words">{c.title || t("my.classes.untitled")}</h2>
          <p className="mt-1 text-sm text-[color:var(--ink-2)]">{t("student.result.objectives")}</p>
          <ul className="mt-3 flex flex-col gap-2">
            {c.seen.map((lo) => (
              <li key={lo.lo_id} className="flex items-start gap-3 text-sm">
                <span aria-hidden className="w-4 shrink-0 text-base leading-6" style={{ color: BAND_TONE[lo.band] }}>
                  {BAND_GLYPH[lo.band]}
                </span>
                <span className="min-w-0 flex-1 break-words">
                  <span className="mr-2 text-xs text-[color:var(--ink-2)]">{lo.code}</span>
                  {lo.text}
                </span>
                <span className="shrink-0 text-xs text-[color:var(--ink-2)]">{t(`me.bands.${lo.band}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="text-xs text-[color:var(--ink-2)]">{t("student.result.privacy")}</p>
    </section>
  );
}
